import '@/styles/tailwind-core.module.css';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect, useState } from 'react';
import { FaArrowLeft, FaCheck, FaCopy } from 'react-icons/fa6';

import Footer from '@/common/footer';
import Header from '@/common/header';
import { notify } from '@/components/ui/notify';
import { BTN_TINT_SM } from '@/constants/button-classes';
import { PRO_SETTINGS_API_URL } from '@/constants/pro-settings-api';
import NextSeoComponent from '@/layouts/next-seo-component';
import { Main } from '@/templates/main';
import { buildCanonical } from '@/utils/seo';

interface Game {
  id: string;
  name: string;
  image: string;
}

interface PlayerData {
  id: string;
  name: string;
  team: string;
  image: string;
  games: Game[];
}

interface Setting {
  id: string;
  name: string;
  value: Record<string, string>;
}

interface PlayerProSettingsProps {
  playerData: PlayerData;
}

export default function PlayerProSettings({ playerData }: PlayerProSettingsProps) {
  const router = useRouter();
  const { t } = useTranslation(['pages/pro-settings', 'components/titles']);
  const [selectedGameId, setSelectedGameId] = useState(playerData.games[0]?.id);
  const [gameData, setGameData] = useState<Setting[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (id: string, command: string) => {
    navigator.clipboard.writeText(command);
    notify.success(t('copied_toast'));
    setCopiedId(id);
    setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
  };

  useEffect(() => {
    if (selectedGameId) {
      const fetchGameData = async () => {
        try {
          const response = await fetch(`${PRO_SETTINGS_API_URL}/settings/players/${playerData.id}/game/${selectedGameId}`);
          if (!response.ok) throw new Error(`pro-settings API returned ${response.status}`);

          const { data } = await response.json();
          setGameData(data || []);
        } catch {
          // Keep the page usable: the player header stays, the settings list is
          // simply empty instead of the whole render blowing up.
          setGameData([]);
        }
      };
      fetchGameData();
    }
  }, [selectedGameId, playerData.id]);

  return (
    <>
      <Header variant='primary' />
      <Main className='pro-settings-arena-page max-w-full overflow-x-hidden bg-[var(--bg-0)] bg-[linear-gradient(180deg,#050607_0%,#0a0c0e_46%,#050607_100%)] text-[var(--fg)]' meta={<NextSeoComponent title={`${playerData.name} — ${t('pro-settings', { ns: 'components/titles' })}`} description={t('pro-settings', { ns: 'components/titles' })} canonical={buildCanonical(router.locale, `/pro-settings/${playerData.id}`)} structuredDataType='product' productName={t('pro-settings', { ns: 'components/titles' })} productDescription={t('pro-settings', { ns: 'components/titles' })} />}>
        <section className='mx-auto flex w-full max-w-[1400px] flex-col gap-8 p-5 xl:pb-20 xl:pt-10'>
          <h1 className='font-display text-[clamp(30px,4.5vw,56px)] font-bold leading-[1.02] text-white'>
            {playerData.name} <span className='text-[var(--green)]'>{t('player_settings')}</span>
          </h1>

          <div className='flex w-full flex-col gap-8 xl:flex-row xl:items-start'>
            {/* Player card (same as listing) — games double as the settings selector */}
            <div className='flex w-full flex-col gap-4 xl:max-w-screen-sm-portrait'>
              <div className='relative flex aspect-[3/4] flex-col justify-end overflow-hidden rounded-[16px] border border-white/20 bg-[var(--bg-2)] bg-[linear-gradient(160deg,rgba(156,255,46,0.2),rgba(138,91,255,0.2))]'>
                <Image src={playerData.image} alt={playerData.name} fill sizes='320px' className='object-cover object-top' />
                <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,transparent_42%,rgba(9,8,14,0.55)_70%,rgba(9,8,14,0.92)_100%)]' />
                <div aria-hidden='true' className='absolute inset-0 rounded-[16px] ring-1 ring-inset ring-white/5' />
                <div className='relative z-10 flex flex-col gap-0.5 p-4'>
                  <p className='font-display truncate text-[17px] font-black leading-tight text-white'>{playerData.name}</p>
                  <p className='truncate font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--fg-mute)]'>{playerData.team}</p>
                  {playerData.games.length > 0 && (
                    <div className='mt-2 flex flex-col gap-1'>
                      {playerData.games.map((game) => {
                        const active = selectedGameId === game.id;
                        return (
                          <button key={game.id} type='button' aria-pressed={active} onClick={() => setSelectedGameId(game.id)} className={`flex items-center gap-1.5 text-start text-[13px] font-semibold transition-colors ${active ? 'text-[var(--green)]' : 'text-[var(--fg-dim)] hover:text-[var(--fg)]'}`}>
                            <span aria-hidden='true' className={`size-1.5 shrink-0 rounded-full ${active ? 'bg-[var(--green)]' : 'bg-white/30'}`} />
                            <span className='truncate'>{game.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <Link href='/pro-settings' className={`${BTN_TINT_SM} w-full`}>
                <FaArrowLeft size={13} aria-hidden='true' className='rtl:-scale-x-100' />
                {t('go_back')}
              </Link>
            </div>

            {/* Settings */}
            <div className='flex w-full flex-col gap-5'>
              {gameData.map((setting) => {
                const command = Object.entries(setting.value)
                  .map(([key, value]) => `${key} ${value}`)
                  .join('; ');
                const copied = copiedId === setting.id;

                return (
                  <div key={setting.id} className='overflow-hidden rounded-[16px] border border-white/10 bg-white/[0.03]'>
                    <div className='flex items-center justify-between gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-4'>
                      <p className='font-mono text-[13px] font-black uppercase tracking-[0.12em] text-[var(--fg-dim)]'>{setting.name.replace(/_/g, ' ')}</p>
                      <button onClick={() => handleCopy(setting.id, command)} className={`${BTN_TINT_SM} shrink-0`}>
                        {copied ? <FaCheck size={14} aria-hidden='true' /> : <FaCopy size={14} aria-hidden='true' />}
                        {copied ? t('copied') : t('copy')}
                      </button>
                    </div>
                    <div className='flex flex-col gap-5 p-5'>
                      <div className='grid gap-x-5 gap-y-4 sm:grid-cols-2 xl:grid-cols-3'>
                        {Object.entries(setting.value).map(([key, value]) => (
                          <div key={key} className='flex flex-col gap-0.5'>
                            <p className='text-[12px] uppercase tracking-[0.06em] text-[var(--fg-mute)]'>{key.replace(/_/g, ' ')}</p>
                            <p className='font-medium text-[var(--fg)]'>{value}</p>
                          </div>
                        ))}
                      </div>
                      <p className='overflow-x-auto rounded-[10px] border border-white/10 bg-black/30 px-4 py-2.5 font-mono text-[13px] text-[var(--fg-dim)]'>{command};</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </Main>
      <Footer />
    </>
  );
}

export async function getStaticProps({ params, locale }: { params: { slug: string }; locale: string }) {
  try {
    const playerResponse = await fetch(`${PRO_SETTINGS_API_URL}/settings/players/${params.slug}`);
    if (!playerResponse.ok) throw new Error(`pro-settings API returned ${playerResponse.status} for player ${params.slug}`);

    const { data: playerData } = await playerResponse.json();
    if (!playerData) throw new Error(`pro-settings API returned no player for ${params.slug}`);

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common', 'pages/pro-settings', 'components/titles'])),
        playerData,
      },
      revalidate: 60,
    };
  } catch {
    // `revalidate` keeps the 404 short-lived: the page comes back on its own
    // once the API recovers, without a deploy.
    return { notFound: true, revalidate: 60 };
  }
}

export async function getStaticPaths() {
  try {
    const response = await fetch(`${PRO_SETTINGS_API_URL}/settings/players`);
    if (!response.ok) throw new Error(`pro-settings API returned ${response.status}`);

    const { data } = await response.json();
    const paths = (data ?? []).map((player: { id: string }) => ({ params: { slug: player.id } }));

    return { paths, fallback: 'blocking' };
  } catch {
    // The pro-settings API must never be able to fail the whole build (it took
    // the production build down when staging answered 5xx with an HTML page).
    // With no paths, `fallback: 'blocking'` renders each page on first visit.
    return { paths: [], fallback: 'blocking' };
  }
}
